/**
 * Authentication State Store (Zustand)
 * Manages JWT tokens, user profile, and token expiry
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserProfile } from '../api/types';

export interface AuthState {
  // Token state
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null; // Unix timestamp (milliseconds)

  // User state
  user: UserProfile | null;

  // Actions
  setTokens: (accessToken: string, refreshToken: string, expiresIn: number) => void;
  clearTokens: () => void;
  setUser: (user: UserProfile) => void;
  isTokenExpired: () => boolean;
  shouldRefresh: () => boolean;
}

/**
 * Auth store with localStorage persistence
 * Tokens and user data survive page refreshes
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      user: null,

      // Set tokens after login or refresh
      setTokens: (accessToken: string, refreshToken: string, expiresIn: number) => {
        const expiresAt = Date.now() + expiresIn * 1000;
        set({ accessToken, refreshToken, expiresAt });
      },

      // Clear tokens on logout
      clearTokens: () => {
        set({
          accessToken: null,
          refreshToken: null,
          expiresAt: null,
          user: null,
        });
      },

      // Set user profile
      setUser: (user: UserProfile) => {
        set({ user });
      },

      // Check if token is expired
      isTokenExpired: () => {
        const { expiresAt } = get();
        if (!expiresAt) return true;
        return Date.now() >= expiresAt;
      },

      // Check if token should be refreshed (5 min buffer)
      shouldRefresh: () => {
        const { expiresAt } = get();
        if (!expiresAt) return false;
        const bufferMs = 5 * 60 * 1000; // 5 minutes
        return Date.now() >= expiresAt - bufferMs;
      },
    }),
    {
      name: 'splister-auth', // localStorage key
      partialize: (state) => ({
        // Only persist tokens and user, not computed state
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        expiresAt: state.expiresAt,
        user: state.user,
      }),
    }
  )
);
